import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiFetch";
import { studioConfigContract } from "@shared/contracts";
// Belt-and-suspenders: remove the dev override key when running in production
// so a key left in localStorage by a developer can never affect real users.
// The literal key is intentionally inlined here (not imported from devConstants)
// so Vite can fully tree-shake devConstants.ts — and therefore DevFlagPanel —
// from the production bundle.
const _DEV_PROFILE_OVERRIDE_KEY = "devProfileOverride";
if (!import.meta.env.DEV) {
  try {
    localStorage.removeItem(_DEV_PROFILE_OVERRIDE_KEY);
  } catch {
    // localStorage unavailable — nothing to clean up
  }
}

export interface StudioConfigData {
  name: string;
  shortName: string;
  brandSuffix?: string;
  slogan?: string;
  heroTagline?: string;
  description?: string;
  established?: number;
  address: string;
  city: string;
  state: string;
  zip: string;
  fullAddress: string;
  phone: string;
  contactEmail: string;
  siteUrl?: string;
  heroImage?: string;
  heroBlackBg?: boolean;
  logoUrl?: string;
  logoBlackBg?: boolean;
  footerImageUrl?: string;
  footerBlackBg?: boolean;
  footerImageAdjustments?: { positionX?: number; positionY?: number; brightness?: number; contrast?: number; opacity?: number; rotate?: number; zoom?: number };
  heroImageAdjustments?: { positionX?: number; positionY?: number; brightness?: number; contrast?: number; opacity?: number; rotate?: number; zoom?: number };
  teamPhotoUrl?: string;
  teamBlackBg?: boolean;
  teamPhotoAdjustments?: { positionX?: number; positionY?: number; brightness?: number; contrast?: number; opacity?: number; rotate?: number; zoom?: number };
  hours?: string;
  hoursNote?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  externalGalleryUrl?: string;
  lat?: string;
  lng?: string;
  mapUrl?: string;
  defaultDeposit?: number;
  shopUrl?: string;
  onboardingComplete?: boolean;
  privacyPolicyUrl?: string | null;
  bookingSlotStartHour?: number;
  bookingSlotEndHour?: number;
  bookingSlotIntervalMinutes?: number;
  lightroomShowcases?: Array<{ name: string; embedUrl: string; externalUrl?: string }>;
  heroVideoUrl?: string | null;
  heroVideoLoopEnabled?: boolean;
  heroVideoLoopStart?: number;
  heroVideoLoopEnd?: number | null;
  heroVideoPlaylist?: Array<{ url: string; loopStart: number; loopEnd: number | null }> | null;
  cancellationWindowHours?: number | null;
  parkingNotes?: string | null;
  referralDescription?: string | null;
  depositMinimumAmount?: number | null;
  depositPercentage?: number | null;
  backgroundTileUrl?: string | null;
  /**
   * Active deployment profile. Defaults to "client" when APP_PROFILE is unset.
   *
   * "agency" — B2B marketing surface; booking/portal/gallery routes are blocked
   *            via the IS_AGENCY_BUILD whitelist in App.tsx.
   * "client" — full studio site (the default for any client or clone deployment).
   * "demo"   — behaviorally identical to "client" on the frontend. No isDemo
   *            guards exist; all isAgency checks treat demo === client (not agency).
   *            The value exists so seed-loader.ts can select seed-demo.json for
   *            the agency demo Replit without overwriting a real seed-data.json.
   */
  profile?: "agency" | "client" | "demo";
}

const FALLBACK: StudioConfigData = {
  name: "Tattoo Studio",
  shortName: "Studio",
  brandSuffix: "Tattoo",
  slogan: "",
  heroTagline: "",
  description: "Professional tattoo studio.",
  address: "",
  city: "",
  state: "",
  zip: "",
  fullAddress: "",
  phone: "",
  contactEmail: "",
  siteUrl: "",
  heroImage: "",
  hours: "",
  hoursNote: "",
  instagramUrl: "",
  facebookUrl: "",
  externalGalleryUrl: "",
  lat: "",
  lng: "",
  mapUrl: "",
  defaultDeposit: undefined,
  onboardingComplete: false,
};

// v2: profile is no longer persisted (see writeCache), so bump the key to
// invalidate any client caches that still have the old shape.
const LS_KEY = "studio_config_v2";
const LS_TTL = 5 * 60 * 1000;

function readCache(): { data: StudioConfigData; ts: number } | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { data: StudioConfigData; ts: number };
    if (Date.now() - entry.ts > LS_TTL) {
      localStorage.removeItem(LS_KEY);
      return null;
    }
    return entry;
  } catch {
    // intentionally silent
    return null;
  }
}

function writeCache(data: StudioConfigData): void {
  try {
    // Strip `profile` before persisting — it is sourced from the APP_PROFILE
    // server env var and must never be served from a stale local snapshot.
    // Omitting it here means any env-var change takes effect on the very next
    // network fetch, without waiting for the cache TTL to expire.
    const { profile: _profile, ...cacheable } = data;
    localStorage.setItem(LS_KEY, JSON.stringify({ data: cacheable, ts: Date.now() }));
  } catch (e) {
    // localStorage may be unavailable (private browsing, storage quota); silently skip caching.
    console.debug("[studio-config] localStorage write skipped:", e);
  }
}

async function fetchStudioConfig(): Promise<StudioConfigData> {
  const cached = readCache();
  if (cached) return cached.data;
  try {
    const raw = await apiFetch(studioConfigContract);
    // The contract response is structurally a superset of StudioConfigData
    // (every consumed field is now declared on the contract schema), so
    // narrowing through `Partial<StudioConfigData>` lets the type system
    // confirm the assignment without an unsafe cast.
    const data: StudioConfigData = { ...FALLBACK, ...(raw as Partial<StudioConfigData>) };
    writeCache(data);
    return data;
  } catch {
    return FALLBACK;
  }
}

// "demo" is kept valid so VITE_APP_PROFILE=demo does not fall through to
// undefined and cause a flash. On the frontend, demo === client in every
// guard — there are no isDemo branches anywhere.
const VALID_PROFILES = new Set<string>(["client", "agency", "demo"]);

// VITE_APP_PROFILE is baked into the bundle at build time (set via Replit env
// vars). Reading it here — at module parse time — means the profile is known
// on the very first render with zero API latency, eliminating the flash of
// the wrong homepage before the /api/studio-config fetch completes.
const BUILD_PROFILE = (() => {
  const v = import.meta.env.VITE_APP_PROFILE;
  return v && VALID_PROFILES.has(v) ? (v as StudioConfigData["profile"]) : undefined;
})();

/** Exported for testing. Applies build-time VITE_APP_PROFILE then dev localStorage override. */
export function applyProfileOverrides(data: StudioConfigData): StudioConfigData {
  // 1. Build-time env var wins first (no latency, no flash).
  if (BUILD_PROFILE) return { ...data, profile: BUILD_PROFILE };

  // 2. Dev-only localStorage override (cleared in production by the guard above).
  if (import.meta.env.DEV) {
    try {
      const override = localStorage.getItem(_DEV_PROFILE_OVERRIDE_KEY);
      if (override && VALID_PROFILES.has(override)) {
        return { ...data, profile: override as StudioConfigData["profile"] };
      }
    } catch {
      // localStorage unavailable — silently skip
    }
  }

  // 3. Fall through to whatever the API returned (APP_PROFILE server env var).
  return data;
}


function getPlaceholderData(): StudioConfigData {
  return applyProfileOverrides(readCache()?.data ?? FALLBACK);
}

export function useStudioConfig() {
  const { data: raw = FALLBACK, isLoading } = useQuery<StudioConfigData>({
    queryKey: ["/api/studio-config"],
    queryFn: fetchStudioConfig,
    placeholderData: getPlaceholderData,
    staleTime: LS_TTL,
    refetchOnWindowFocus: false,
  });

  return { config: applyProfileOverrides(raw), isLoading };
}
